import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IMultiCharts } from './Imulti-charts';
import { area, axisBottom, axisLeft, curveBasis, format, line, scaleLinear, select } from 'd3';

@Component({
  selector: 'app-multi-charts',
  templateUrl: './multi-charts.component.html',
  styleUrls: ['./multi-charts.component.scss'],
})
export class MultiChartsComponent implements OnChanges {
  @Input() chartConfig: IMultiCharts;
  @Output() emitChart: EventEmitter<any> = new EventEmitter();
  @ViewChild('chartContainer', { static: true }) chartContainer: ElementRef;
  @ViewChild('chartParent', { static: true }) chartParent: ElementRef;
  chartName = '';
  noDataShown = false;

  ngOnChanges(changes?: SimpleChanges): void {
    void changes;
    setTimeout(() => {
      if (!this.chartConfig.dataIsChanged) {
        this.drawNoData();
      } else {
        let checkData = false;

        for (const element of this.chartConfig.multiCharts!) {
          if (element.data!.length) {
            checkData = true;
          }
        }

        if (checkData) {
          this.draw();
        } else {
          this.drawNoData();
        }
      }
    }, 100);
  }

  drawNoData(): void {
    const svg = select(this.chartContainer.nativeElement);
    const div = select(this.chartParent.nativeElement);

    svg.selectAll('g').remove();
    div.selectAll('div').remove();

    this.noDataShown = true;
  }

  draw(): void {
    this.noDataShown = false;

    const svg = select(this.chartContainer.nativeElement)
      .attr('width', this.chartConfig.width)
      .attr('height', this.chartConfig.height);
    const div = select(this.chartParent.nativeElement);

    select(this.chartContainer.nativeElement).selectAll('g').remove();
    select(this.chartContainer.nativeElement).selectAll('text').remove();
    select(this.chartContainer.nativeElement).selectAll('rect').remove();
    select(this.chartParent.nativeElement).selectAll('div').remove();

    const width = this.chartConfig.width;
    const height = this.chartConfig.height;

    const xValue = this.chartConfig.xAxisProp;
    const yValue = this.chartConfig.yAxisProp;
    const yAxisLabel = this.chartConfig.yAxisTitle;

    const margin = { top: 20, right: 20, bottom: 90, left: 60 };
    const innerWidth = width! - margin.left - margin.right;
    const innerHeight = height! - margin.top - margin.bottom;

    const xScale = scaleLinear().domain(this.chartConfig.xAxisRange).range([0, innerWidth]);
    const yScale = scaleLinear().domain(this.chartConfig.yAxisRange).range([innerHeight, 0]);

    const lineTooltip = div.append('div').attr('class', 'chart-tooltip').style('opacity', 0);

    const mousemove = (e) => {
      if (
        xScale(xScale.invert([e.offsetX - margin.left])) < 0 ||
        xScale(xScale.invert([e.offsetX - margin.left])) > innerWidth
      ) {
        g.selectAll('.hoverLine').attr('stroke', 'transparent').attr('fill', 'transparent');
        svg.selectAll('circle').attr('fill', 'transparent');
        lineTooltip.transition().duration(200).style('opacity', 0);

        return;
      }

      g.selectAll('.hoverLine')
        .attr('x1', xScale(xScale.invert([e.offsetX - margin.left])))
        .attr('y1', 0)
        .attr('x2', xScale(xScale.invert(e.offsetX - margin.left)))
        .attr('y2', innerHeight)
        .attr('stroke', '#147F90')
        .attr('fill', '#A6E8F2');
      lineTooltip.attr('style', null);
      lineTooltip.transition().duration(200).style('opacity', 1);

      lineTooltip.html(
        '<div class="mb-1">Days to departure: ' + Math.round(xScale.invert(e.offsetX - margin.left)) + '</div>',
      );

      if (xScale(xScale.invert(e.offsetX - margin.left)) + 220 >= innerWidth) {
        lineTooltip.attr('style', null);
        lineTooltip
          .style('top', '70px')
          .style('right', innerWidth - Math.round(xScale(xScale.invert(e.offsetX - margin.left))) + 50 + 'px');
      } else {
        lineTooltip
          .style('top', '70px')
          .style('left', Math.round(xScale(xScale.invert(e.offsetX - margin.left)) + 70) + 'px');
      }

      const draftKeys = lineTooltip.selectAll('div');

      for (const key of this.chartConfig.multiCharts!) {
        let leadTime = null;

        draftKeys
          .append('p')
          .attr('class', key.label)
          .classed('mb-1', true)
          .append('span')
          .text(key.label + ': ');

        if (key.type === 'stroke-line') {
          leadTime = key.data![0];
        } else {
          leadTime = key.data!.filter((item) => item.dtd == Math.round(xScale.invert(e.offsetX - margin.left)))[0];
        }

        if (leadTime) {
          switch (this.chartConfig.yAxisFormat) {
            case 'percentage':
              draftKeys
                .selectAll('.' + key.label)
                .append('span')
                .text(format('.0%')(leadTime.percentage.toFixed(2)));
              break;
            default:
              draftKeys
                .selectAll('.' + key.label)
                .append('span')
                .text(leadTime.percentage.toFixed(2));
          }
        }
      }

      // draw points on hover line for tooltip with all accurate values
      for (let index = 0; index < this.chartConfig.multiCharts!.length; index++) {
        const element = this.chartConfig.multiCharts![index];
        let percentageOFDate = null;

        if (element.type === 'stroke-line') {
          percentageOFDate = element.data![0];
        } else {
          percentageOFDate = element.data!.filter(
            (item) => item.dtd === Math.round(xScale.invert(e.offsetX - margin.left)),
          )[0];
        }

        if (percentageOFDate) {
          svg
            .selectAll('.hoverPoint' + index)
            .attr('cx', xScale(xScale.invert(e.offsetX - margin.left)))
            .attr('cy', yScale(percentageOFDate.percentage))
            .attr('r', '7')
            .attr('fill', '#147F90');
        } else {
          svg.selectAll('.hoverPoint' + index).attr('fill', 'transparent');
        }
      }
    };

    const focus = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    const g = focus.append('g').attr('transform', `translate(0,40)`);

    for (const element of this.chartConfig.multiCharts!) {
      if (element.data!.length) {
        switch (element.type) {
          case 'area':
            const areaGenerator = area()
              .x((d) => xScale(d[xValue!]))
              .y0(innerHeight)
              .y1((d) => yScale(d[yValue!]))
              .curve(curveBasis);

            g.append('path')
              .attr('class', 'line-path')
              .datum(element.data)
              .style('fill', element.fill)
              .style('opacity', '.8')
              .attr('d', areaGenerator(element.data));
            break;
          case 'line':
            const lineGenerator = line()
              .x((d) => xScale(d[xValue!]))
              .y((d) => yScale(d[yValue!]))
              .curve(curveBasis);

            g.append('path')
              .attr('class', 'line-path')
              .datum(element.data)
              .style('stroke', element.fill)
              .style('stroke-width', 5)
              .style('fill', 'none')
              .style('stroke-linejoin', 'round')
              .attr('d', lineGenerator(element.data));
            break;
          case 'stroke-line':
            g.append('line')
              .style('stroke', element.fill)
              .style('stroke-width', 5)
              .style('fill', 'none')
              .style('stroke-linejoin', 'round')
              .attr('x1', 0)
              .attr('y1', yScale(element.data![0][yValue!]))
              .attr('x2', xScale(this.chartConfig.xAxisRange![1]))
              .attr('y2', yScale(element.data![0][yValue!]));
            break;
          default:
        }

        g.on('mousemove', (e) => mousemove(e));
      }
    }

    if (this.chartConfig.legend) {
      const labelY = 20;
      const chartData = this.chartConfig.multiCharts!.reverse();

      for (let index = 0; index < chartData.length; index++) {
        const element = this.chartConfig.multiCharts![index];
        svg
          .append('rect')
          .attr('x', (innerWidth / chartData.length) * index + margin.left)
          .attr('y', labelY - 10)
          .attr('r', 6)
          .style('fill', element.fill)
          .attr('width', 16)
          .attr('height', 16)
          .attr('rx', 4);
        svg
          .append('text')
          .attr('x', (innerWidth / chartData.length) * index + margin.left + 30)
          .attr('y', labelY)
          .text(element.label)
          .style('font-size', '15px')
          .attr('alignment-baseline', 'middle');
      }
    }

    const xAxis = axisBottom(xScale)
      .ticks(3)
      .tickValues(this.chartConfig.xAxisRange)
      .tickFormat((d) => {
        return d + ' days';
      })
      .tickPadding(10);

    const yAxis = axisLeft(yScale)
      .ticks(5)
      .tickSize(-innerWidth)
      .tickFormat((d) => {
        return format(',.0%')(d);
      })
      .tickPadding(5);

    const yAxisG = g.append('g').call(yAxis);
    yAxisG.selectAll('.domain').remove();

    yAxisG
      .append('text')
      .attr('class', 'axis-label')
      .attr('y', -60)
      .attr('x', -innerHeight / 2)
      .attr('fill', 'black')
      .attr('transform', `rotate(-90)`)
      .attr('text-anchor', 'middle')
      .text(yAxisLabel);

    const xAxisGA = g.append('g').attr('class', 'xAxis').call(xAxis).attr('transform', `translate(0,${innerHeight})`);

    g.append('line').classed('hoverLine', true);
    g.append('text').attr('text-anchor', 'middle').attr('class', 'myLabel'); // easy to style with CSS
    g.append('rect').attr('fill', 'transparent').attr('x', 0).attr('y', 0).attr('width', width).attr('height', height);

    xAxisGA.selectAll('.tick').each(function (i, d): any {
      const tickX = select(this).node().transform.baseVal.consolidate().matrix.e;
      let tickXNext = 0;

      xAxisGA.selectAll('.tick').each(function (data, idx): any {
        if (d + 1 === idx) {
          tickXNext = select(this).node().transform.baseVal.consolidate().matrix.e;
        }
      });

      if (tickXNext !== 0 && tickXNext - tickX < 47) {
        select(this).attr('opacity', 0);
      }
    });

    for (let index = 0; index < this.chartConfig.multiCharts!.length; index++) {
      g.append('circle').classed('hoverPoint' + index, true);
    }
  }
}
